// src/pages/workflow/WorkFlowEdit.jsx
import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import {
  CornerUpLeft,
  Save,
  Undo,
  Redo,
  PlusCircle,
  AlertTriangle,
  ArrowLeft,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  UserPlus,
  GripHorizontal,
  CalendarClock,
  ClipboardType,
  Hash,
  Image,
  Key,
  Mail,
  Palette,
  Phone,
  QrCode,
  Receipt,
  SquareArrowOutUpRight,
  SquareDashedKanban,
  Text,
  TicketCheckIcon,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiCall } from "../../Apis/apiCall";
import LoadingComponent from "../../components/ui/LoadingComponent";
import CustomSelect from "../../components/common/CustomSelect";
import { handleApiResponse, handleCustomResponse } from "../../utils/response";
import WorkflowEditGuide from "../../components/WorkflowEditGuide";

const htmlComponents = [
  {
    name: "",
    label: "",
    displayName: "Text",
    icon: ClipboardType,
    type: "text",
    validation: "min:0|max:255",
  },
  {
    name: "",
    label: "",
    displayName: "Email",
    icon: Mail,
    type: "email",
    validation: "email",
  },
  {
    name: "",
    label: "",
    displayName: "Number",
    icon: Hash,
    type: "number",
    validation: "min:0|max:1000000",
  },
  {
    name: "",
    label: "",
    displayName: "Phone Number",
    icon: Phone,
    type: "tel",
    validation: "tel",
  },
  {
    name: "",
    label: "",
    displayName: "Textbox",
    icon: Text,
    type: "textarea",
    validation: "min:0|max:1000",
  },
  {
    name: "",
    label: "",
    displayName: "Dropdown",
    icon: ChevronDown,
    type: "select",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "Plate Number",
    icon: SquareDashedKanban,
    type: "plate_number",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "Date Time",
    icon: CalendarClock,
    type: "datetime",
    validation: "date_format:Y-m-d H:i:s",
  },
  {
    name: "",
    label: "",
    displayName: "Image Upload",
    icon: Image,
    type: "image",
    validation: "mimes:jpg,png|max:2048",
  },
  {
    name: "",
    label: "",
    displayName: "Color Picker",
    icon: Palette,
    type: "color",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "QR Scan",
    icon: QrCode,
    type: "scan_qr",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "QR Code",
    icon: QrCode,
    type: "qr_code",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "OTP",
    icon: Key,
    type: "otp",
    validation: "digits:6",
  },
  {
    name: "",
    label: "",
    displayName: "Popup",
    icon: SquareArrowOutUpRight,
    type: "popup",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "Receipt",
    icon: Receipt,
    type: "receipt",
    validation: "",
  },
  {
    name: "",
    label: "",
    displayName: "Ticket Summary",
    icon: TicketCheckIcon,
    type: "ticket_summary",
    validation: "",
  },
].map((c, i) => ({
  ...c,
  step_field_id: i + 1,
  required: false,
  options: null,
  order: i + 1,
}));

const WorkflowNameNode = memo(({ data }) => {
  return (
    <div className="workflow-name-node shadow-4">
      <p className="fs-5">{data.label}</p>
      <Handle
        type="source"
        position={Position.Bottom}
        className="custom-handle"
      />
    </div>
  );
});
const CustomStep = memo(({ id, data }) => {
  const handleDragStart = (event, fieldId) => {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({ fieldId, nodeId: id })
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const rawData = event.dataTransfer.getData("application/json");

    // Check if data exists and is not empty
    if (!rawData || rawData.trim() === "") {
      return;
    }

    let draggedData;
    try {
      draggedData = JSON.parse(rawData);
    } catch (error) {
      handleCustomResponse("error", "Invalid drag-and-drop data");
      return;
    }
    const { fieldId: draggedFieldId, nodeId: sourceNodeId } = draggedData;

    // Cross-step drag & drop
    if (sourceNodeId !== id) {
      // Field ko source step se remove karo aur current step mein add karo
      data.onMoveFieldToOtherStep?.(draggedFieldId, sourceNodeId, id);
      return;
    }

    // Same step ke andar reordering - sirf tab karo jab fields hain
    if (data.fields?.length > 0) {
      const targetFieldElement = event.target.closest(".field-item");
      if (!targetFieldElement) return;

      const targetFieldId = targetFieldElement.dataset.fieldId;
      if (!draggedFieldId || !targetFieldId) return;

      const updatedFields = [...data.fields];
      const draggedIndex = updatedFields.findIndex(
        (f) => f.id === draggedFieldId
      );
      const targetIndex = updatedFields.findIndex(
        (f) => f.id === targetFieldId
      );

      if (draggedIndex === -1 || targetIndex === -1) return;

      const [draggedField] = updatedFields.splice(draggedIndex, 1);
      updatedFields.splice(targetIndex, 0, draggedField);

      const reorderedFields = updatedFields.map((f, index) => ({
        ...f,
        order: index + 1,
      }));

      data.onUpdateFields(id, reorderedFields);
    }
  };

  const handleMoveField = (fieldId, direction) => {
    const updatedFields = [...data.fields];
    const index = updatedFields.findIndex((f) => f.id === fieldId);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === updatedFields.length - 1)
    ) {
      return;
    }

    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [updatedFields[index], updatedFields[swapIndex]] = [
      updatedFields[swapIndex],
      updatedFields[index],
    ];

    const reorderedFields = updatedFields.map((f, i) => ({
      ...f,
      order: i + 1,
    }));

    data.onUpdateFields(id, reorderedFields);
  };

  return (
    <div
      className="custom-node-wrapper shadow-4"
      style={{
        "--node-border-color-hover": "#cbd5e1",
        "--node-border-color": "#e5e7eb",
      }}
    >
      <div
        className="custom-node-header"
        style={{
          background:
            "linear-gradient(135deg, #862509 0%, #f85930 50%, #f99e5f 100%)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-white">{data.label}</div>
          <div className="d-flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                data.onEditStep(id);
              }}
              className="custom-node-action-btn"
              title="Edit Step"
            >
              <Edit size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                data.onAssignStep(id);
              }}
              className="custom-node-action-btn assign-staff-btn"
              title="Assign Staff"
            >
              <UserPlus size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                data.onRemoveNode(id);
              }}
              className="custom-node-action-btn danger"
              title="Delete Step"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
      <div className="content-container">
        <div className="scroll-shadow-top" />
        <div className="scroll-shadow-bottom" />
        {data.fields?.length > 0 ? (
          <div
            className="custom-scrollbar field-list"
            onScroll={(e) => {
              const container = e.target.closest(".content-container");
              if (container) {
                container.dataset.scrollTop = e.target.scrollTop > 10;
                container.dataset.scrollBottom =
                  e.target.scrollTop <
                  e.target.scrollHeight - e.target.clientHeight - 10;
              }
            }}
          >
            {data.fields
              .sort((a, b) => a.order - b.order)
              .map((field, index) => (
                <div
                  key={field.id}
                  data-field-id={field.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field.id)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="field-item"
                >
                  <div className="drag-handle">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, index >= 0 ? "down" : "up");
                      }}
                      disabled
                      style={{ cursor: "pointer" }}
                      title="Drag"
                    >
                      <GripHorizontal size={24} />
                    </button>
                  </div>
                  <span className="field-label">
                    <div className="field-dot" />
                    <span className="truncate">
                      {field.label || field.name}
                    </span>
                    {field.required && (
                      <span className="required-asterisk">*</span>
                    )}
                  </span>
                  <div className="field-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, "up");
                      }}
                      disabled={index === 0}
                      className="field-action-btn"
                      title="Move Up"
                    >
                      <ChevronUp size={10} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveField(field.id, "down");
                      }}
                      disabled={index === data.fields.length - 1}
                      className="field-action-btn"
                      title="Move Down"
                    >
                      <ChevronDown size={10} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        data.onEditField(field, id);
                      }}
                      className="field-action-btn"
                      title="Edit Field"
                    >
                      <Edit size={10} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        data.onDeleteField(field.id, id);
                      }}
                      className="field-action-btn"
                      title="Delete Field"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div
            className="no-fields-placeholder"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ cursor: "pointer", minHeight: "100px" }}
          >
            <div className="no-fields-icon-bg">
              <div className="no-fields-icon-inner" />
            </div>
            <div className="fw-bold text-secondary-light text-xs">
              No fields yet
            </div>
            <div className="text-xs text-neutral-400">
              Drag components or fields from other steps here to start.
            </div>
          </div>
        )}
      </div>
      <Handle type="target" position={Position.Top} className="custom-handle" />
      <Handle
        type="source"
        position={Position.Bottom}
        className="custom-handle"
      />
    </div>
  );
});
const FieldConfigModal = ({
  isOpen,
  onClose,
  field,
  onSave,
  isEditMode,
  fieldNames,
  optionSources,
}) => {
  const [config, setConfig] = useState({
    name: "",
    label: "",
    required: false,
    validation: "",
    min: "",
    max: "",
    type: "",
    options: "",
    options_source: null,
    options_label_field: null,
    options_value_field: null,
    order: 1,
  });
  const [optionSourceType, setOptionSourceType] = useState("");
  const [selectedApiSource, setSelectedApiSource] = useState(null); // Changed to null for consistency
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  useEffect(() => {
    if (field) {
      let min = "";
      let max = "";
      if (
        ["text", "number", "textarea"].includes(field.type) &&
        field.validation
      ) {
        const validationParts = field.validation.split("|");
        validationParts.forEach((part) => {
          if (part.startsWith("min:")) min = part.split(":")[1];
          if (part.startsWith("max:")) max = part.split(":")[1];
        });
      }
      setConfig({
        name: field.name || "",
        label: field.label || "",
        required: field.required || false,
        validation: field.validation || "",
        min: min,
        max: max,
        type: field.type || "",
        options: Array.isArray(field.options)
          ? field.options.join("|")
          : field.options || "",
        options_source: field.options_source || null,
        options_label_field: field.options_label_field || null,
        options_value_field: field.options_value_field || null,
        order: field.order || 1,
      });
      setOptionSourceType(
        field.options_source
          ? "api"
          : field.options && field.options.length > 0
          ? "hard_coded"
          : ""
      );
      // Find the full API source object from optionSources.api
      const apiSource = field.options_source
        ? optionSources.api.find(
            (source) => source.url === field.options_source
          )
        : null;
      setSelectedApiSource(apiSource || null);
    }
  }, [field, optionSources]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSourceTypeChange = (selectedOption) => {
    const type = selectedOption?.value;
    setOptionSourceType(type);
    setConfig((prev) => ({
      ...prev,
      options: "",
      options_source: null,
      options_label_field: null,
      options_value_field: null,
    }));
    setSelectedApiSource(null); // Clear selected API source
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!config.label.trim()) {
      handleCustomResponse("error", "Field Label cannot be empty.");
      return;
    }
    if (!config.name) {
      handleCustomResponse("error", "Please select a Field Name.");
      return;
    }
    if (config.type === "select") {
      if (
        optionSourceType === "hard_coded" &&
        (!config.options || config.options.trim() === "")
      ) {
        handleCustomResponse(
          "error",
          "Please add at least one option for hardcoded dropdown."
        );
        return;
      }
      if (optionSourceType === "api" && !selectedApiSource) {
        handleCustomResponse(
          "error",
          "Please select an API source for the dropdown."
        );
        return;
      }
    }
    if (["text", "number", "textarea"].includes(config.type)) {
      if (config.min && isNaN(config.min)) {
        handleCustomResponse("error", "Minimum value must be a number.");
        return;
      }
      if (config.max && isNaN(config.max)) {
        handleCustomResponse("error", "Maximum value must be a number.");
        return;
      }
      if (
        config.min &&
        config.max &&
        parseFloat(config.min) > parseFloat(config.max)
      ) {
        handleCustomResponse(
          "error",
          "Minimum value cannot be greater than maximum value."
        );
        return;
      }
    }

    const validation = ["text", "number", "textarea"].includes(config.type)
      ? [config.min && `min:${config.min}`, config.max && `max:${config.max}`]
          .filter(Boolean)
          .join("|")
      : config.validation;

    const fieldToSave = {
      ...field,
      ...config,
      validation,
      options:
        config.type === "select" &&
        optionSourceType === "hard_coded" &&
        config.options
          ? config.options.split("|").map((opt) => opt.trim())
          : null,
      options_source:
        optionSourceType === "api" ? selectedApiSource?.url : null,
      options_label_field:
        optionSourceType === "api" ? selectedApiSource?.label_field : null,
      options_value_field:
        optionSourceType === "api" ? selectedApiSource?.value_field : null,
    };

    onSave(fieldToSave);
    onClose();
  };

  if (!isOpen) return null;

  const fieldNameOptions = fieldNames.map((field) => ({
    value: field.field_name,
    label: field.field_name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
  }));

  const sourceTypeOptions = [
    { value: "hard_coded", label: "Write By Own" },
    { value: "api", label: "Get By API" },
  ];

  const apiSourceOptions = optionSources.api.map((source) => ({
    value: source.url, // Use URL as the value for simplicity
    label: source.label,
  }));

  // const showValidationInputs = ["text", "number", "textarea"].includes(
  //   config.type
  // );
  const showValidationInputs = ["no-text"].includes(config.type);

  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md"
        style={{ width: "450px" }}
      >
        <h3 className="mb-24">{isEditMode ? "Edit Field" : "Add Field"}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-16">
            <label className="form-label fw-bold">Field Name</label>
            <CustomSelect
              options={fieldNameOptions}
              value={fieldNameOptions.find((opt) => opt.value === config.name)}
              onChange={(selectedOption) => {
                handleChange({
                  target: {
                    name: "name",
                    value: selectedOption?.value || "",
                  },
                });
                handleChange({
                  target: {
                    name: "label",
                    value: selectedOption?.label || "",
                  },
                });
              }}
              placeholder="-- Select Field Name --"
              isSearchable
            />
          </div>
          <div className="mb-16">
            <label className="form-label fw-bold">Field Label</label>
            <input
              type="text"
              className="form-control"
              name="label"
              value={config.label}
              onChange={handleChange}
              required
            />
          </div>
          {showValidationInputs && (
            <div className="mb-16">
              <label className="form-label fw-bold">Validation</label>
              <div className="row">
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    name="min"
                    min={0}
                    value={config.min}
                    onChange={handleChange}
                    placeholder="Min Length/Value"
                  />
                </div>
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    name="max"
                    value={config.max}
                    onChange={handleChange}
                    placeholder="Max Length/Value"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mb-16 form-check d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input"
              id="required-checkbox"
              name="required"
              checked={config.required}
              onChange={handleChange}
            />
            <label
              htmlFor="required-checkbox"
              className="form-check-label ms-2"
            >
              Required
            </label>
          </div>
          {config.type === "select" && (
            <div className="mb-16">
              <label className="form-label fw-bold">Options Source</label>
              <CustomSelect
                options={sourceTypeOptions}
                value={sourceTypeOptions.find(
                  (opt) => opt.value === optionSourceType
                )}
                onChange={handleSourceTypeChange}
                placeholder="-- Select Source Type --"
              />
              {optionSourceType === "hard_coded" && (
                <div className="mt-16">
                  <label className="form-label fw-bold">
                    Dropdown Options (pipe-separated)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="options"
                    value={config.options}
                    onChange={handleChange}
                    placeholder="e.g., option1|option2|option3"
                  />
                </div>
              )}
              {optionSourceType === "api" && (
                <div
                  className="mt-16"
                  style={{ marginBottom: isSelectOpen ? "250px" : "" }}
                >
                  <label className="form-label fw-bold">
                    Select API Source
                  </label>
                  <CustomSelect
                    options={apiSourceOptions}
                    value={
                      apiSourceOptions.find(
                        (opt) => opt.value === selectedApiSource?.url
                      ) || null
                    }
                    onChange={(selected) => {
                      setSelectedApiSource(
                        selected
                          ? optionSources.api.find(
                              (source) => source.url === selected.value
                            )
                          : null
                      );
                    }}
                    placeholder="-- Select API --"
                    isClearable
                    onMenuOpen={() => setIsSelectOpen(true)} // ✅ Yahan use karo
                    onMenuClose={() => setIsSelectOpen(false)} // ✅ Yahan use karo
                  />
                </div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-24">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-600">
              {isEditMode ? "Update Field" : "Add Field"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignModal = ({
  isOpen,
  onClose,
  step,
  onSave,
  staffList,
  selectedBranch,
  selectedLocation,
}) => {
  const [selectedStaff, setSelectedStaff] = useState([]);

  useEffect(() => {
    if (step && step.assigned_to) {
      setSelectedStaff(
        step.assigned_to.map((staff) => ({
          value: staff.id,
          label: staff.name,
          role: staff.role,
          profile_picture: staff.profile_picture,
          initials: staff.name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase(),
        }))
      );
    } else {
      setSelectedStaff([]);
    }
  }, [step]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedStaff.length) {
      handleCustomResponse("error", "Please select at least one staff member.");
      return;
    }
    onSave({
      ...step,
      assigned_to: selectedStaff.map((staff) => ({
        id: staff.value,
        name: staff.label,
        role: staff.role,
        profile_picture: staff.profile_picture,
      })),
    });
    onClose();
  };

  if (!isOpen) return null;

  const staffOptions = staffList.map((staff) => ({
    value: staff.staff_id,
    label: staff.staff_name,
    role: staff.staff_role,
    profile_picture: staff.profile_picture,
    initials: staff.staff_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase(),
  }));
  const data = {
    branch_id: selectedBranch?.value,
    location_id: selectedLocation?.value,
    branchName: selectedBranch?.label,
    locationName: selectedLocation?.label,
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md d-flex flex-column"
        style={{
          width: "500px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          minHeight: "70vh",
        }}
      >
        <div className="p-16 border-bottom bg-neutral-50">
          <h5 className="m-0">Assign Step</h5>
        </div>
        <form
          onSubmit={handleSubmit}
          className="d-flex flex-column flex-grow-1 overflow-hidden"
        >
          <div className="p-24 flex-grow-1">
            <div className="mb-16">
              <label className="form-label fw-bold">Select Staff</label>
              <CustomSelect
                isMulti
                options={staffOptions}
                value={selectedStaff}
                onChange={setSelectedStaff}
                placeholder="Search staff members..."
                isSearchable
                addNewLabel="Assign Staff"
                addNewRoute="/staff-branch-assign"
                state={true}
                stateData={data}
              />
            </div>
            <div className="mt-16">
              <h6 className="fw-bold">
                Assigned Staff ({selectedStaff.length})
              </h6>
              {selectedStaff.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {selectedStaff.map((staff) => (
                    <div
                      key={staff.value}
                      className="badge bg-primary-50 text-light-600 d-flex align-items-center p-2"
                    >
                      <span className="p-4">{staff.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-secondary-light small">
                  No staff assigned yet
                </div>
              )}
            </div>
          </div>
          <div className="d-flex justify-content-end gap-12 p-16 border-top">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary-600"
              disabled={selectedStaff.length === 0}
            >
              Assign{" "}
              {selectedStaff.length > 0 ? `(${selectedStaff.length})` : ""}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// Utility Functions
const generateUniqueCode = (label, existingCodes) => {
  let baseCode =
    label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .trim() || "step";
  let uniqueCode = baseCode;
  let counter = 1;
  while (existingCodes.includes(uniqueCode)) {
    uniqueCode = `${baseCode}_${counter}`;
    counter++;
  }
  return uniqueCode;
};

// Step Update Modal
const StepUpdateModal = ({ isOpen, onClose, step, onSave }) => {
  const [stepLabel, setStepLabel] = useState(step?.label || "");
  const [stepLabelError, setStepLabelError] = useState("");

  useEffect(() => {
    if (step) {
      setStepLabel(step.label);
      setStepLabelError("");
    }
  }, [step]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!stepLabel.trim()) {
      setStepLabelError("Step label cannot be empty.");
      return;
    }
    onSave({ ...step, label: stepLabel });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md"
        style={{ width: "400px" }}
      >
        <h3 className="mb-24">Update Step</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-16">
            <label className="form-label fw-bold">Step Label</label>
            <input
              type="text"
              className={`form-control ${stepLabelError ? "is-invalid" : ""}`}
              value={stepLabel}
              onChange={(e) => {
                setStepLabel(e.target.value);
                setStepLabelError("");
              }}
              required
            />
            {stepLabelError && (
              <div className="invalid-feedback">{stepLabelError}</div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-24">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-600">
              Update Step
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Generic Deletion Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content-wrapper card-shadow-md">
        <h5 className="mb-16">{title}</h5>
        <p>{message}</p>
        <div className="d-flex justify-content-end gap-2 mt-24">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Unsaved Changes Confirmation Modal
const UnsavedChangesModal = ({ isOpen, onClose, onSave, onDiscard }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md text-center"
        style={{ width: "450px" }}
      >
        <AlertTriangle size={48} className="text-warning-500 mx-auto mb-16" />
        <h5 className="mb-2">Unsaved Changes</h5>
        <p className="text-secondary-light">
          You have unsaved changes. Do you want to save them before leaving?
        </p>
        <div className="d-flex justify-content-center gap-3 mt-24">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onDiscard}
          >
            Discard
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary-600"
            onClick={onSave}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkFlowEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workflow } = location.state || {};
  const workflowId = workflow?.workflow_id || null;
  const workflowName = workflow?.name || "No Workflow Selected";

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [stepLabel, setStepLabel] = useState("");
  const [stepLabelError, setStepLabelError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [fieldConfigModal, setFieldConfigModal] = useState({
    isOpen: false,
    field: null,
    isEditMode: false,
    nodeId: null,
  });
  const [stepUpdateModal, setStepUpdateModal] = useState({
    isOpen: false,
    step: null,
    nodeId: null,
  });
  const [assignModal, setAssignModal] = useState({
    isOpen: false,
    step: null,
    nodeId: null,
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    nodeId: null,
  });
  const [deletedNodes, setDeletedNodes] = useState([]);
  const [optionSources, setOptionSources] = useState({ master: [], api: [] });
  const [fieldNames, setFieldNames] = useState([]);
  const [stepSuggestions, setStepSuggestions] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState(null);
  const initialWorkflowState = useRef(null);
  const isInitialLoad = useRef(true);

  // Ref to store static data
  const staticDataRef = useRef({
    stepSuggestions: [],
    optionSources: { master: [], api: [] },
    fieldNames: [],
  });

  const saveToHistory = useCallback(
    (currentNodes, currentEdges, isInitialLoad = false) => {
      const stateToSave = {
        nodes: JSON.parse(JSON.stringify(currentNodes)),
        edges: JSON.parse(JSON.stringify(currentEdges)),
        isInitialState: isInitialLoad,
      };

      setHistory((prev) => {
        if (isInitialLoad) {
          return [stateToSave];
        } else {
          const newHistory = prev.slice(0, historyIndex + 1);
          return [...newHistory, stateToSave];
        }
      });

      setHistoryIndex((prev) => (isInitialLoad ? 0 : prev + 1));
    },
    [historyIndex]
  );

  const recalculateLayout = useCallback((nodesToLayout) => {
    const workflowNameNode = nodesToLayout.find(
      (n) => n.type === "workflowName"
    );
    const customNodes = nodesToLayout
      .filter((n) => n.type === "custom")
      .sort((a, b) => a.data.step_order - b.data.step_order);

    let currentY = 100;
    const xLeft = 100;
    const xRight = 500;
    const baseNodeHeight = 150;
    const fieldHeight = 60;

    const laidOutNodes = customNodes.map((node, index) => {
      const isEven = index % 2 === 0;
      const xPos = isEven ? xLeft : xRight;

      const nodeHeight =
        baseNodeHeight + (node.data.fields?.length || 0) * fieldHeight;

      const updatedNode = {
        ...node,
        position: { x: xPos, y: currentY },
        data: { ...node.data, step_order: index + 1 },
      };

      currentY += nodeHeight + 50;
      return updatedNode;
    });

    const finalNodes = workflowNameNode
      ? [workflowNameNode, ...laidOutNodes]
      : laidOutNodes;

    const newEdges = [];
    if (laidOutNodes.length > 0) {
      if (workflowNameNode) {
        newEdges.push({
          id: `edge-workflowName-${laidOutNodes[0].id}`,
          source: "workflowName",
          target: laidOutNodes[0].id,
          animated: true,
          style: { stroke: "#4b5563", strokeWidth: 2 },
        });
      }
      for (let i = 0; i < laidOutNodes.length - 1; i++) {
        newEdges.push({
          id: `edge-${laidOutNodes[i].id}-${laidOutNodes[i + 1].id}`,
          source: laidOutNodes[i].id,
          target: laidOutNodes[i + 1].id,
          animated: true,
          style: { stroke: "#4b5563", strokeWidth: 2 },
        });
      }
    }

    return { finalNodes, finalEdges: newEdges };
  }, []);

  const updateWorkflowState = useCallback(
    (nodesToUpdate, saveHistory = true) => {
      const { finalNodes, finalEdges } = recalculateLayout(nodesToUpdate);
      setNodes(finalNodes);
      setEdges(finalEdges);
      if (saveHistory) {
        saveToHistory(finalNodes, finalEdges);
      }
    },
    [recalculateLayout, setNodes, setEdges, saveToHistory]
  );

  useEffect(() => {
    if (initialWorkflowState.current) {
      const currentState = JSON.stringify({ nodes, edges });
      if (currentState !== initialWorkflowState.current) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
    }
  }, [nodes, edges]);

  const handleNavigation = (target) => {
    if (isDirty) {
      if (historyIndex > 0) {
        setNavigationTarget(target);
        setShowUnsavedModal(true);
      } else {
        navigate(-1);
      }
    } else {
      navigate(target);
    }
  };

  const handleDiscard = () => {
    setShowUnsavedModal(false);
    navigate(navigationTarget);
  };

  const handleSaveAndNavigate = async () => {
    await handleSaveWorkflow();
    setShowUnsavedModal(false);
    navigate(navigationTarget);
  };

  const nodeTypes = {
    custom: (props) => (
      <CustomStep
        {...props}
        data={{
          ...props.data,
          onUpdateFields: handleUpdateFields,
          onMoveFieldToOtherStep: handleMoveFieldToOtherStep, // ✅ Add this line
          onRemoveNode: (id) =>
            setDeleteConfirmModal({ isOpen: true, nodeId: id }),
          onEditField: handleEditField,
          onDeleteField: handleDeleteField,
          onEditStep: handleEditStep,
          onAssignStep: handleAssignStep,
        }}
      />
    ),
    workflowName: WorkflowNameNode,
  };

  const fetchStaticData = useCallback(async () => {
    try {
      const [stepSuggestionsRes, apiRes, fieldNamesRes] = await Promise.all([
        apiCall("get", `/workflow/template/step-suggestions`, null, navigate),
        apiCall("get", `/get-option-source-data/2`, null, navigate),
        apiCall("get", `/workflow-field-names`, null, navigate),
      ]);

      if (stepSuggestionsRes.data.success) {
        staticDataRef.current.stepSuggestions = stepSuggestionsRes.data.data;
        setStepSuggestions(stepSuggestionsRes.data.data);
      }
      staticDataRef.current.optionSources = {
        master: [],
        api: apiRes.data.data,
      };
      staticDataRef.current.fieldNames = fieldNamesRes.data.data;
      setOptionSources(staticDataRef.current.optionSources);
      setFieldNames(staticDataRef.current.fieldNames);
    } catch (error) {
      handleCustomResponse(
        "error",
        "Failed to load static configuration data."
      );
    }
  }, [navigate]);

  const fetchWorkFlow = useCallback(
    async (branchId, locationId) => {
      if (!workflowId) {
        handleCustomResponse("error", "No workflow selected");
        navigate("/workflows");
        return;
      }
      setIsLoading(true);
      try {
        const url =
          branchId && locationId
            ? `/workflow-steps-fields-v2/${workflowId}?branch_id=${branchId}&location_id=${locationId}`
            : `/workflow-steps-fields-v2/${workflowId}`;

        const res = await apiCall("get", url, null, navigate);
        const workflowData = res.data.data;

        // Set branches
        const assignedBranchesLocations =
          workflowData.assigned_branches_locations || [];
        const uniqueBranches = [
          ...new Map(
            assignedBranchesLocations.map((item) => [
              item.branch_id,
              { value: item.branch_id, label: item.branch_name },
            ])
          ).values(),
        ];
        setBranches(uniqueBranches);

        // Filter locations based on selected branch
        let filteredLocations = [];
        if (selectedBranch) {
          filteredLocations = assignedBranchesLocations
            .filter((item) => item.branch_id === selectedBranch.value)
            .map((item) => ({
              value: item.location_id,
              label: item.location_name,
            }));
        } else {
          // If no branch is selected, show all unique locations
          filteredLocations = [
            ...new Map(
              assignedBranchesLocations.map((item) => [
                item.location_id,
                { value: item.location_id, label: item.location_name },
              ])
            ).values(),
          ];
        }
        setLocations(filteredLocations);

        // Set default branch and location on initial load
        if (isInitialLoad.current && uniqueBranches.length > 0) {
          const defaultBranch = uniqueBranches[0];
          setSelectedBranch(defaultBranch);

          const defaultLocations = assignedBranchesLocations
            .filter((item) => item.branch_id === defaultBranch.value)
            .map((item) => ({
              value: item.location_id,
              label: item.location_name,
            }));

          if (defaultLocations.length > 0) {
            setSelectedLocation(defaultLocations[0]);
          } else {
            setSelectedLocation(null);
          }
          isInitialLoad.current = false;
        }

        // Validate selectedLocation
        if (
          selectedLocation &&
          !filteredLocations.some((loc) => loc.value === selectedLocation.value)
        ) {
          setSelectedLocation(null);
        }

        const workflowNameNode = {
          id: "workflowName",
          type: "workflowName",
          position: { x: 100, y: 0 },
          data: { label: workflowName },
          draggable: false,
          selectable: false,
        };

        const steps = workflowData.steps || [];
        const newNodes = [
          workflowNameNode,
          ...steps.map((step, index) => ({
            id: String(step.step_id),
            type: "custom",
            position: { x: 0, y: 0 },
            data: {
              step_id: step.step_id,
              code: step.step_code,
              label: step.step_label,
              step_order: index + 1,
              assigned_to:
                step.assigned_to?.map((staff) => ({
                  id: staff.staff_id,
                  name: staff.staff_name,
                  branch_id: staff.branch_id || null,
                  branch_name: staff.branch_name || null,
                  location_id: staff.location_id || null,
                  location_name: staff.location_name || null,
                  step_access_id: staff.step_access_id || null,
                })) || [],
              fields:
                step.fields?.map((field, idx) => ({
                  id: String(field.field_id || `client-${Date.now()}-${idx}`),
                  field_id: field.field_id,
                  name: field.name,
                  label: field.label,
                  type: field.type,
                  required: field.required,
                  validation: field.validation,
                  options:
                    field.options && field.options !== "[]"
                      ? JSON.parse(field.options)
                      : null,
                  options_source: field.options_source,
                  options_label_field: field.options_label_field,
                  options_value_field: field.options_value_field,
                  order: field.order || idx + 1,
                })) || [],
            },
          })),
        ];

        const { finalNodes, finalEdges } = recalculateLayout(newNodes);
        setNodes(finalNodes);
        setEdges(finalEdges);

        const initialState = JSON.stringify({
          nodes: finalNodes,
          edges: finalEdges,
        });
        initialWorkflowState.current = initialState;
        setIsDirty(false);

        saveToHistory(finalNodes, finalEdges, true);
      } catch (error) {
        handleCustomResponse("error", "Failed to fetch workflow data.");
        navigate("/workflows");
      } finally {
        setIsLoading(false);
      }
    },
    [
      workflowId,
      workflowName,
      navigate,
      recalculateLayout,
      saveToHistory,
      selectedBranch,
      selectedLocation,
    ]
  );

  const fetchStaffList = useCallback(async () => {
    if (!selectedLocation?.value) {
      setStaffList([]);
      return;
    }
    try {
      const res = await apiCall(
        "post",
        "/get-staff-by-location-id",
        { location_id: selectedLocation.value },
        navigate
      );
      if (res.data.success) {
        setStaffList(res.data.data);
      } else {
        handleCustomResponse(
          "error",
          res.data.message || "Error fetching staff list."
        );
      }
    } catch (error) {
      handleCustomResponse(
        "error",
        "An error occurred while fetching the staff list."
      );
    }
  }, [navigate, selectedLocation]);

  useEffect(() => {
    fetchStaticData();
    fetchWorkFlow();
  }, []);

  useEffect(() => {
    if (!isInitialLoad.current) {
      fetchWorkFlow(selectedBranch?.value, selectedLocation?.value);
      fetchStaffList();
    }
  }, [selectedBranch, selectedLocation, fetchStaffList]);

  const validateStep = (label, excludeNodeId = null) => {
    if (!label.trim()) return "Step label is required.";
    if (
      nodes.some(
        (node) => node.data.label === label && node.id !== excludeNodeId
      )
    )
      return "Step label must be unique.";
    return "";
  };

  const handleAddStep = () => {
    const error = validateStep(stepLabel);
    if (error) {
      setStepLabelError(error);
      return;
    }
    setStepLabelError("");

    const customNodes = nodes.filter((n) => n.type === "custom");
    const newCode = generateUniqueCode(
      stepLabel,
      nodes.map((n) => n.data.code)
    );

    const newNode = {
      id: `node-${uuidv4()}`,
      type: "custom",
      position: { x: 0, y: 0 },
      data: {
        step_id: null,
        code: newCode,
        label: stepLabel,
        step_order: customNodes.length + 1,
        assigned_to: [],
        fields: [],
      },
    };

    updateWorkflowState([...nodes, newNode]);
    setStepLabel("");
    handleCustomResponse("success", `Step "${stepLabel}" added.`);
  };

  const handleAddSuggestedStep = (suggestion) => {
    const error = validateStep(suggestion.template_label);
    if (error) {
      handleCustomResponse("error", error);
      return;
    }

    const customNodes = nodes.filter((n) => n.type === "custom");
    const newCode = generateUniqueCode(
      suggestion.template_label,
      nodes.map((n) => n.data.code)
    );

    const newNode = {
      id: `node-${uuidv4()}`,
      type: "custom",
      position: { x: 0, y: 0 },
      data: {
        step_id: null,
        code: newCode,
        label: suggestion.template_label,
        step_order: customNodes.length + 1,
        assigned_to: [],
        fields: suggestion.fields.map((field, idx) => ({
          id: `new-${uuidv4()}`,
          field_id: null,
          name: field.name,
          label: field.label,
          type: field.type,
          required: field.required,
          validation: field.validation,
          options: field.options ? JSON.parse(field.options) : null,
          options_source: field.options_source,
          options_label_field: field.options_label_field,
          options_value_field: field.options_value_field,
          order: idx + 1,
        })),
      },
    };

    updateWorkflowState([...nodes, newNode]);
    handleCustomResponse(
      "success",
      `Suggested step "${suggestion.template_label}" added.`
    );
  };

  const handleEditStep = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) setStepUpdateModal({ isOpen: true, step: node.data, nodeId });
  };

  const handleSaveStep = (updatedStep) => {
    const error = validateStep(updatedStep.label, stepUpdateModal.nodeId);
    if (error) {
      handleCustomResponse("error", error);
      return;
    }

    const newCode = generateUniqueCode(
      updatedStep.label,
      nodes
        .filter((n) => n.id !== stepUpdateModal.nodeId)
        .map((n) => n.data.code)
    );

    const updatedNodes = nodes.map((node) =>
      node.id === stepUpdateModal.nodeId
        ? {
            ...node,
            data: { ...node.data, label: updatedStep.label, code: newCode },
          }
        : node
    );

    setNodes(updatedNodes);
    saveToHistory(updatedNodes, edges);
    handleCustomResponse("success", `Step "${updatedStep.label}" updated.`);
  };

  const handleSaveFieldConfig = (updatedField) => {
    const { nodeId, isEditMode } = fieldConfigModal;
    setNodes((nds) => {
      const newNodes = nds.map((node) => {
        if (node.id === nodeId) {
          const isDuplicate = node.data.fields.some(
            (f) => f.name === updatedField.name && f.id !== updatedField.id
          );
          if (isDuplicate) {
            handleCustomResponse(
              "error",
              "Field name already exists in this step."
            );
            return node;
          }

          const newFields = isEditMode
            ? node.data.fields.map((f) =>
                f.id === updatedField.id
                  ? { ...updatedField, order: f.order }
                  : f
              )
            : [
                ...node.data.fields,
                {
                  ...updatedField,
                  id: `new-${uuidv4()}`,
                  field_id: null,
                  order: node.data.fields.length + 1,
                },
              ];

          const sortedFields = newFields
            .sort((a, b) => a.order - b.order)
            .map((f, index) => ({ ...f, order: index + 1 }));

          return { ...node, data: { ...node.data, fields: sortedFields } };
        }
        return node;
      });

      const { finalNodes, finalEdges } = recalculateLayout(newNodes);
      setEdges(finalEdges);
      saveToHistory(finalNodes, finalEdges);
      return finalNodes;
    });
    handleCustomResponse(
      "success",
      isEditMode ? "Field updated" : "Field added"
    );
  };

  const handleEditField = (field, nodeId) => {
    setFieldConfigModal({ isOpen: true, field, isEditMode: true, nodeId });
  };

  const handleDeleteField = useCallback(
    (fieldId, nodeId) => {
      setNodes((nds) => {
        const newNodes = nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  fields: node.data.fields
                    .filter((f) => f.id !== fieldId)
                    .map((f, index) => ({ ...f, order: index + 1 })),
                },
              }
            : node
        );

        const { finalNodes, finalEdges } = recalculateLayout(newNodes);
        setEdges(finalEdges);
        saveToHistory(finalNodes, finalEdges);
        return finalNodes;
      });
      handleCustomResponse("success", "Field successfully removed");
    },
    [setNodes, setEdges, saveToHistory, recalculateLayout]
  );

  const handleUpdateFields = useCallback(
    (nodeId, reorderedFields) => {
      const sortedFields = reorderedFields.map((field, index) => ({
        ...field,
        order: index + 1,
      }));
      setNodes((nds) => {
        const newNodes = nds.map((node) => {
          if (node.id === nodeId) {
            return { ...node, data: { ...node.data, fields: sortedFields } };
          }
          return node;
        });

        const { finalNodes, finalEdges } = recalculateLayout(newNodes);
        setEdges(finalEdges);
        saveToHistory(finalNodes, finalEdges);
        return finalNodes;
      });
      handleCustomResponse("success", "Fields reordered within step.");
    },
    [setNodes, setEdges, saveToHistory, recalculateLayout]
  );

  const handleAssignStep = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) setAssignModal({ isOpen: true, step: node.data, nodeId });
  };

  const handleSaveAssign = (updatedStep) => {
    const updatedNodes = nodes.map((node) =>
      node.id === assignModal.nodeId
        ? {
            ...node,
            data: { ...node.data, assigned_to: updatedStep.assigned_to },
          }
        : node
    );
    setNodes(updatedNodes);
    saveToHistory(updatedNodes, edges);
    handleCustomResponse(
      "success",
      `Staff assigned to step "${updatedStep.label}".`
    );
  };

  const handleRemoveNode = useCallback(
    (nodeId) => {
      const nodeToRemove = nodes.find((node) => node.id === nodeId);
      if (nodeToRemove) {
        setDeletedNodes((prev) => [...prev, nodeToRemove]);
        const remainingNodes = nodes.filter((node) => node.id !== nodeId);
        updateWorkflowState(remainingNodes);
        handleCustomResponse(
          "info",
          `Step "${nodeToRemove.data.label}" moved to trash.`
        );
        setSelectedNode(null);
      }
    },
    [nodes, updateWorkflowState]
  );

  const handleRestoreNode = useCallback(
    (nodeId) => {
      const nodeToRestore = deletedNodes.find((node) => node.id === nodeId);
      if (!nodeToRestore) return;

      const error = validateStep(nodeToRestore.data.label);
      if (error) {
        handleCustomResponse("error", `Cannot restore: ${error}`);
        return;
      }

      const customNodes = nodes.filter((n) => n.type === "custom");
      const restoredNode = {
        ...nodeToRestore,
        data: {
          ...nodeToRestore.data,
          step_id: null,
          step_order: customNodes.length + 1,
          fields: nodeToRestore.data.fields.map((field) => ({
            ...field,
            field_id: null,
            id: `new-${uuidv4()}`,
          })),
        },
      };

      updateWorkflowState([...nodes, restoredNode]);
      setDeletedNodes((prev) => prev.filter((node) => node.id !== nodeId));
      handleCustomResponse(
        "success",
        `Step "${nodeToRestore.data.label}" restored.`
      );
    },
    [nodes, deletedNodes, updateWorkflowState, validateStep]
  );

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      setHistoryIndex(newIndex);
      // handleCustomResponse("info", "Action undone");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIndex(newIndex);
      // handleCustomResponse("info", "Action redone");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleSaveWorkflow = async () => {
    setSaveStatus("saving");
    setIsLoading(true);
    try {
      const sortedNodes = [...nodes]
        .filter((node) => node.type === "custom")
        .sort((a, b) => a.data.step_order - b.data.step_order);

      const workflowData = {
        workflow_id: workflowId,
        steps: sortedNodes.map((node, stepIdx) => {
          let accessAssignments = null;
          const hasAssignmentContext =
            selectedBranch?.value && selectedLocation?.value;
          const hasStaff =
            node.data.assigned_to && node.data.assigned_to.length > 0;

          if (hasAssignmentContext && hasStaff) {
            accessAssignments = [
              {
                id: node.data.assigned_to[0]?.step_access_id || null,
                branch_id: selectedBranch.value,
                location_id: selectedLocation.value,
                staff_ids: node.data.assigned_to.map((staff) => staff.id),
              },
            ];
          }

          return {
            step_id: node.data.step_id || null,
            step_code: node.data.code,
            step_label: node.data.label,
            step_order: stepIdx + 1,
            access_assignments: accessAssignments,
            fields: node.data.fields
              .sort((a, b) => a.order - b.order)
              .map((field, fieldIdx) => ({
                field_id: field.field_id || null,
                name: field.name || "",
                label: field.label || "",
                type: field.type || "",
                required: field.required || false,
                validation: field.validation || "",
                options: field.options ? field.options : null,
                options_source: field.options_source || null,
                options_label_field: field.options_label_field || null,
                options_value_field: field.options_value_field || null,
                field_order: fieldIdx + 1,
              })),
          };
        }),
      };

      const res = await apiCall(
        "post",
        "/insert-workflow-step-fields-v2",
        workflowData,
        navigate
      );
      handleApiResponse(res.data, "Save WorkFlow", "POST");
      if (res.data.success) {
        setSaveStatus("success");
        await fetchWorkFlow(selectedBranch?.value, selectedLocation?.value);
      } else {
        throw new Error(res.data.message || "Failed to save workflow");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to Save WorkFlow", "POST");
      setSaveStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  const onDragStart = (event, component) => {
    event.dataTransfer.setData(
      "application/reactflow",
      JSON.stringify(component)
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      // Check if data exists before parsing
      const reactFlowData = event.dataTransfer.getData("application/reactflow");
      if (!reactFlowData) {
        return;
      }

      if (!reactFlowInstance || !selectedNode) {
        handleCustomResponse(
          "warning",
          "Please select a step to add fields to."
        );
        return;
      }

      let component;
      try {
        component = JSON.parse(reactFlowData);
      } catch (error) {
        handleCustomResponse("error", "Invalid drag data");
        return;
      }

      const nextFieldOrder = selectedNode.data.fields.length;
      setFieldConfigModal({
        isOpen: true,
        field: { ...component, order: nextFieldOrder },
        isEditMode: false,
        nodeId: selectedNode.id,
      });
      handleCustomResponse("info", `Configuring new ${component.type} field.`);
    },
    [reactFlowInstance, selectedNode]
  );

  const onNodeDragStop = useCallback(
    (event, draggedNode) => {
      if (draggedNode.type === "workflowName") return;

      const sortedByY = nodes
        .filter((n) => n.type === "custom")
        .sort((a, b) => a.position.y - b.position.y)
        .map((node, index) => ({
          ...node,
          data: { ...node.data, step_order: index + 1 },
        }));

      const workflowNameNode = nodes.find((n) => n.type === "workflowName");
      const nodesForLayout = workflowNameNode
        ? [workflowNameNode, ...sortedByY]
        : sortedByY;

      updateWorkflowState(nodesForLayout);
    },
    [nodes, updateWorkflowState]
  );

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) => {
        const newEdge = {
          ...params,
          animated: true,
          style: { stroke: "#4b5563", strokeWidth: 2 },
        };
        return addEdge(newEdge, eds);
      }),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    if (node.type !== "workflowName") setSelectedNode(node);
  }, []);

  const handleBranchChange = (branch) => {
    setSelectedBranch(branch);
    if (!branch) {
      setSelectedLocation(null); // Clear location when branch is cleared
      setLocations([]); // Clear locations list
    } else {
      // Filter locations for the selected branch
      const assignedBranchesLocations =
        workflow?.assigned_branches_locations || [];
      const filteredLocations = assignedBranchesLocations
        .filter((item) => item.branch_id === branch.value)
        .map((item) => ({
          value: item.location_id,
          label: item.location_name,
        }));
      setLocations(filteredLocations);
      // If the current selected location is not in the filtered locations, clear it
      if (
        selectedLocation &&
        !filteredLocations.some((loc) => loc.value === selectedLocation.value)
      ) {
        setSelectedLocation(null);
      }
    }
  };
  // Cross-step field movement handler
  const handleMoveFieldToOtherStep = useCallback(
    (fieldId, sourceNodeId, targetNodeId) => {
      if (sourceNodeId === targetNodeId) return;

      setNodes((nds) => {
        const sourceNode = nds.find((node) => node.id === sourceNodeId);
        const targetNode = nds.find((node) => node.id === targetNodeId);

        if (!sourceNode || !targetNode) return nds;

        const fieldToMove = sourceNode.data.fields.find(
          (f) => f.id === fieldId
        );
        if (!fieldToMove) return nds;

        // Field ko source se remove karo
        const updatedSourceFields = sourceNode.data.fields
          .filter((f) => f.id !== fieldId)
          .map((f, index) => ({ ...f, order: index + 1 }));

        // Field ko target mein add karo
        const newFieldOrder = targetNode.data.fields.length + 1;
        const updatedTargetFields = [
          ...targetNode.data.fields,
          {
            ...fieldToMove,
            id: `moved-${uuidv4()}`,
            field_id: null,
            order: newFieldOrder,
          },
        ];

        const newNodes = nds.map((node) => {
          if (node.id === sourceNodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                fields: updatedSourceFields,
              },
            };
          }
          if (node.id === targetNodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                fields: updatedTargetFields,
              },
            };
          }
          return node;
        });

        const { finalNodes, finalEdges } = recalculateLayout(newNodes);
        setEdges(finalEdges);
        saveToHistory(finalNodes, finalEdges);
        return finalNodes;
      });

      handleCustomResponse("success", "Field moved to another step.");
    },
    [setNodes, setEdges, saveToHistory, recalculateLayout]
  );

  if (isLoading && isInitialLoad.current) {
    return <LoadingComponent />;
  }

  return (
    <>
      <WorkflowEditGuide />
      <div className="d-lg-none text-center p-4 min-vh-100 d-flex flex-column justify-content-center align-items-center bg-neutral-50">
        <div className="max-w-md">
          <h3 className="text-2xl font-semibold text-neutral-800 mb-4">
            Device Compatibility
          </h3>
          <p className="text-neutral-600 mb-6 p-5">
            This workflow editor is optimized for larger screens (large devices
            and up). Please use a desktop or larger tablet for the best
            experience.
          </p>
          <button
            className="btn btn-primary-600 px-18 py-18 rounded-md transition-colors duration-200 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => navigate(-1)}
            aria-label="Go back to previous page"
          >
            <span className="d-flex align-items-center justify-content-center gap-2">
              <ArrowLeft size={18} />
              Go Back
            </span>
          </button>
        </div>
      </div>
      <div className="d-none d-lg-flex flex-column vh-100">
        <header className="row p-3 m-0 align-items-center">
          {/* Back Button - Mobile first */}
          <div className="col-2 col-lg-1 d-flex align-items-center">
            <div className="d-flex align-items-center">
              <span
                onClick={() => handleNavigation("/workflows")}
                className="component-btn"
                style={{ cursor: "pointer" }}
              >
                <ArrowLeft size={24} />
              </span>
            </div>
          </div>

          {/* Branch & Location Selectors - Responsive layout */}
          <div className="col-10 col-lg-4 col-xl-5">
            <div className="d-flex flex-column flex-md-row gap-2 gap-md-3 align-items-start align-items-md-center">
              <div
                className="w-100 w-md-auto flex-grow-1"
                style={{ minWidth: "150px" }}
              >
                <CustomSelect
                  options={branches}
                  value={selectedBranch}
                  onChange={handleBranchChange}
                  placeholder="Select Branch"
                  isClearable
                  addNewLabel="Assign Branch & Location"
                  addNewRoute="/workflow-assign"
                  state={true}
                  isSearchable={false}
                  stateData={{ workflowId, workflowName }}
                />
              </div>
              <div
                className="w-100 w-md-auto flex-grow-1"
                style={{ minWidth: "150px" }}
              >
                <CustomSelect
                  options={selectedBranch ? locations : []}
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder="Select Location"
                  isClearable
                  addNewLabel="Assign Branch & Location"
                  addNewRoute="/workflow-assign"
                  state={true}
                  isSearchable={false}
                  stateData={{ workflowId, workflowName }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons - Responsive adjustments */}
          <div className="col-12 col-lg-7 col-xl-6 mt-2 mt-lg-0">
            <div className="d-flex flex-column flex-sm-row justify-content-start justify-content-lg-end gap-2 align-items-stretch align-items-sm-center">
              {/* Undo/Redo Buttons */}
              <div className="d-flex justify-content-center justify-content-sm-start gap-2 order-2 order-sm-1">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  title="Undo"
                  style={{ minWidth: "40px" }}
                >
                  <Undo size={16} />
                  <span className="d-none d-md-inline ms-1">Undo</span>
                </button>
                <button
                  className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  title="Redo"
                  style={{ minWidth: "40px" }}
                >
                  <Redo size={16} />
                  <span className="d-none d-md-inline ms-1">Redo</span>
                </button>
              </div>

              {/* Save Button */}
              <div className="order-1 order-sm-2 flex-grow-1 flex-sm-grow-0">
                <button
                  className="btn btn-primary-600 w-100 w-sm-auto d-flex align-items-center justify-content-center"
                  onClick={handleSaveWorkflow}
                  disabled={isLoading || saveStatus === "saving"}
                  style={{ minWidth: "140px" }}
                >
                  <Save size={16} className="me-1" />
                  {isLoading || saveStatus === "saving"
                    ? "Please wait..."
                    : "Save Workflow"}
                </button>
              </div>
            </div>
          </div>
        </header>
        <div className="row g-0 flex-grow-1" style={{ minHeight: 0 }}>
          <aside className="col-12 col-lg-2 border-end p-24 d-flex flex-column">
            <div className="flex-grow-1" style={{ overflowY: "auto" }}>
              <p className="text-primary-600 fw-bold mb-16">Add New Step</p>
              <div className="mb-16">
                <input
                  type="text"
                  className={`form-control ${
                    stepLabelError ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Step Label"
                  value={stepLabel}
                  onChange={(e) => {
                    setStepLabel(e.target.value);
                    setStepLabelError("");
                  }}
                  disabled={isLoading}
                />
                {stepLabelError && (
                  <div className="invalid-feedback">{stepLabelError}</div>
                )}
              </div>
              <button
                className="btn btn-primary-600 w-100 mb-24"
                onClick={handleAddStep}
                disabled={isLoading}
              >
                Add Step
              </button>
              <hr className="my-24" />
              <p className="text-primary-600 fw-bold mb-16">Deleted Steps</p>
              {deletedNodes.length > 0 ? (
                <div className="deleted-steps-list">
                  {deletedNodes.map((node) => (
                    <div
                      key={node.id}
                      className="deleted-step-item d-flex align-items-center text-center"
                    >
                      <span className="text-sm text-truncate flex-grow-1 pe-2">
                        {node.data.label}
                      </span>
                      <span
                        className="cursor-pointer"
                        onClick={() => handleRestoreNode(node.id)}
                        title="Restore Step"
                      >
                        <CornerUpLeft size={14} />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="small text-secondary-light mt-2">
                  No deleted steps.
                </p>
              )}
            </div>
          </aside>
          <main
            className="col-12 col-lg-8 d-flex flex-column"
            ref={reactFlowWrapper}
          >
            <div className="p-16 overflow-x-auto suggestion-bar">
              <div className="d-flex gap-2">
                {stepSuggestions.length > 0 ? (
                  stepSuggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="suggestion-chip"
                      onClick={() => handleAddSuggestedStep(suggestion)}
                    >
                      <PlusCircle size={14} className="me-1" />
                      {suggestion.template_label}
                    </div>
                  ))
                ) : (
                  <span className="text-secondary-light small">
                    No suggested steps available.
                  </span>
                )}
              </div>
            </div>
            <div className="flex-grow-1" style={{ minHeight: 0 }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeDragStop={onNodeDragStop}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                fitView
                nodesDraggable={!isLoading}
              >
                <MiniMap
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Controls />
                <Background color="#e2e8f0" gap={20} />
              </ReactFlow>
            </div>
          </main>
          <aside
            className="col-12 col-lg-2 border-start p-24"
            style={{ overflowY: "auto" }}
          >
            <p className="text-primary-600 fw-bold mb-8">
              Available Components
            </p>
            {selectedNode ? (
              <>
                <p className="small mb-16">
                  Drag a field to: <strong>{selectedNode.data.label}</strong>
                </p>
                <div className="component-list">
                  {htmlComponents.map((component) => {
                    const IconComponent = component.icon;
                    return (
                      <button
                        key={component.step_field_id}
                        className="component-btn"
                        draggable
                        onDragStart={(event) => onDragStart(event, component)}
                      >
                        <IconComponent size={16} />
                        <span className="text-sm small">
                          {component.displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <p className="small text-secondary-light mb-16">
                  Select a step to add fields.
                </p>
                <div className="component-list">
                  {htmlComponents.map((component) => {
                    const IconComponent = component.icon;
                    return (
                      <button
                        key={component.step_field_id}
                        className="component-btn"
                        disabled
                      >
                        <IconComponent size={16} />
                        <span className="text-sm small">
                          {component.displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </aside>
        </div>
        <FieldConfigModal
          isOpen={fieldConfigModal.isOpen}
          onClose={() =>
            setFieldConfigModal({
              isOpen: false,
              field: null,
              isEditMode: false,
              nodeId: null,
            })
          }
          field={fieldConfigModal.field}
          onSave={handleSaveFieldConfig}
          isEditMode={fieldConfigModal.isEditMode}
          fieldNames={fieldNames}
          optionSources={optionSources}
        />
        <StepUpdateModal
          isOpen={stepUpdateModal.isOpen}
          onClose={() =>
            setStepUpdateModal({ isOpen: false, step: null, nodeId: null })
          }
          step={stepUpdateModal.step}
          onSave={handleSaveStep}
        />
        <AssignModal
          isOpen={assignModal.isOpen}
          onClose={() =>
            setAssignModal({ isOpen: false, step: null, nodeId: null })
          }
          step={assignModal.step}
          onSave={handleSaveAssign}
          staffList={staffList}
          selectedBranch={selectedBranch}
          selectedLocation={selectedLocation}
        />
        <DeleteConfirmModal
          isOpen={deleteConfirmModal.isOpen}
          onClose={() => setDeleteConfirmModal({ isOpen: false, nodeId: null })}
          onConfirm={() => {
            handleRemoveNode(deleteConfirmModal.nodeId);
            setDeleteConfirmModal({ isOpen: false, nodeId: null });
          }}
          title="Confirm Deletion"
          message="Are you sure you want to delete this step"
        />
        <UnsavedChangesModal
          isOpen={showUnsavedModal}
          onClose={() => setShowUnsavedModal(false)}
          onSave={handleSaveAndNavigate}
          onDiscard={handleDiscard}
        />
      </div>
    </>
  );
};

export default WorkFlowEdit;